import { v4 as uuidv4 } from 'uuid';
import { normalizeHexColor } from './richText.js';
import { normalizeStoredImageItem, revokeLocalImageUrl } from './localImage.js';
import { LOCAL_ASSET } from './localAssets.js';
import { getMediaItemUrl } from './qiniuMedia.js';

export const IMAGE_TRIO_TYPE = 'image-trio';
export const IMAGE_TRIO_ITEM_COUNT = 3;
export const IMAGE_TRIO_DEFAULT_RADIUS = 8;
export const IMAGE_TRIO_GAP = 8;
export const IMAGE_TRIO_DEFAULT_TITLE_COLOR = '#ffffff';

const ITEM_PRESETS = [
	{ title: '日常身体护理' },
	{ title: '柔润不粘' },
	{ title: '轻简三步' },
];

function createDefaultImage(index = 0) {
	const sources = LOCAL_ASSET.imgTrio || [];
	return {
		id: `image-trio-img-${uuidv4()}`,
		filename: `img-trio-${(index % Math.max(sources.length, 1)) + 1}.png`,
		previewUrl: sources[index % sources.length] || '',
		url: '',
		local: false,
	};
}

export function createDefaultImageTrioItem(preset = {}, index = 0) {
	const fallback = ITEM_PRESETS[index] || {};
	return {
		id: uuidv4(),
		title: preset.title ?? fallback.title ?? '',
		titleColor: IMAGE_TRIO_DEFAULT_TITLE_COLOR,
		image: createDefaultImage(index),
		jumpLink: null,
	};
}

export function createDefaultImageTrioItems() {
	return ITEM_PRESETS.map((preset, index) => createDefaultImageTrioItem(preset, index));
}

export function getImageTrioDefaults() {
	return normalizeImageTrioData({
		items: createDefaultImageTrioItems(),
	});
}

export function isImageTrioBlock(blockType) {
	return blockType === IMAGE_TRIO_TYPE;
}

export function getImageTrioImageUrl(image) {
	if (!image) return '';
	return getMediaItemUrl(image);
}

function normalizeCardImage(image, fallback) {
	if (!image?.previewUrl && !image?.url) {
		return fallback ? { ...fallback } : null;
	}
	return normalizeStoredImageItem(image, fallback);
}

export function normalizeImageTrioItem(item = {}, index = 0) {
	const fallback = createDefaultImageTrioItem({}, index);
	return {
		id: item.id || uuidv4(),
		title: item.title ?? fallback.title,
		titleColor: normalizeHexColor(item.titleColor || fallback.titleColor || IMAGE_TRIO_DEFAULT_TITLE_COLOR),
		image: normalizeCardImage(item.image, fallback.image),
		jumpLink: item.jumpLink?.type ? { ...item.jumpLink } : null,
	};
}

export function normalizeImageTrioItems(items = []) {
	if (!Array.isArray(items) || !items.length) {
		return createDefaultImageTrioItems();
	}
	const next = items.slice(0, IMAGE_TRIO_ITEM_COUNT).map((item, index) => (
		normalizeImageTrioItem(item, index)
	));
	while (next.length < IMAGE_TRIO_ITEM_COUNT) {
		next.push(createDefaultImageTrioItem({}, next.length));
	}
	return next;
}

export function normalizeImageTrioData(data = {}) {
	const radius = Number(data.borderRadius);
	return {
		items: normalizeImageTrioItems(data.items),
		borderRadius: Number.isFinite(radius)
			? Math.max(0, radius)
			: IMAGE_TRIO_DEFAULT_RADIUS,
		gap: Number.isFinite(Number(data.gap)) ? Math.max(0, Number(data.gap)) : IMAGE_TRIO_GAP,
	};
}

export function getImageTrioBlockStyle(data = {}, { h5 = false } = {}) {
	const normalized = normalizeImageTrioData(data);
	const pxRatio = h5 ? 50 : 1;
	const radius = normalized.borderRadius;
	const gap = normalized.gap;
	return {
		borderRadius: h5 ? `${radius / pxRatio}rem` : `${radius}px`,
		gap: h5 ? `${gap / pxRatio}rem` : `${gap}px`,
	};
}

export function replaceImageTrioImage(currentImage, file, createLocalImageItem) {
	if (currentImage?.local) {
		revokeLocalImageUrl(currentImage.previewUrl);
	}
	return createLocalImageItem(file);
}
